import React, { useCallback, useEffect, useState } from 'react'
import { Grid, LinearProgress } from '@mui/material'
import { useFormContext, UseFormSetValue } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useAuth from '../../../../hooks/useAuth'
import IssueApi from '../../../../api/IssueApi'
import IssuesGroupApi from '../../../../api/IssuesGroupApi'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import { AllCompanyIssues } from './AllCompanyIssues'
import { IssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssuesGroupEntity } from 'types/IssuesGroupEntity'
import { IssueGroups } from './IssueGroups'
import { FormIssueEntity } from '../../types/FormIssueEntity'

/**
 * Takes a list of entities with groupId attributes and returns an object with the entities grouped by groupId.
 */
const byGroupId = <T extends { groupId: string }>(entities: Array<T>) =>
    entities?.reduce(
        (acc, entity) => {
            if (!acc[entity.groupId]) acc[entity.groupId] = []
            acc[entity.groupId].push(entity)
            return acc
        },
        {} as Record<string, T[]>,
    ) || {}

const flattenGroupList = <T extends object>(issueGroups: Record<string, T[]>) => {
    return Object.values(issueGroups).flat()
}

const isGroupInList = (issueGroups: Array<Array<{ id: string }>>, issuesGroup: Array<{ id: string }>) => {
    return issueGroups.some((g) => {
        if (g.length !== issuesGroup.length) return false
        return g.every((i) => issuesGroup.some((j) => i.id === j.id))
    })
}

const useSetFormGroups = (setValue: UseFormSetValue<FormIssueEntity>) => {
    const setFormGroups = useCallback(
        (groups: Record<string, FormIssueEntity['issueGroups']>) => {
            setValue(
                'issueGroups',
                flattenGroupList(groups).map(
                    (group) =>
                        ({
                            groupId: group.groupId,
                            issueId: group.issueId || null,
                            convertibleId: group.convertibleId || null,
                            type: group.type || 'convertible',
                        }) as FormIssueEntity['issueGroups'][number],
                ),
            )
        },
        [setValue], // Add setValue as a dependency
    )

    return { setFormGroups }
}

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()
const issuesGroupApi = new IssuesGroupApi()

function LinkedIssuesDetails({
    issue = null,
    convertible = null,
    setIsCustomFormDirty,
}: {
    setIsCustomFormDirty: (v: boolean) => void
} & ({ issue: IssueEntity; convertible?: null } | { convertible: ConvertibleEntity; issue?: null })) {
    const { isAuthenticated } = useAuth()
    const { setValue } = useFormContext<FormIssueEntity>()
    const { setFormGroups } = useSetFormGroups(setValue)

    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [convertibles, setConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [issueGroups, setIssueGroups] = useState<Record<string, IssuesGroupEntity[]> | null>(null)
    const companyId = issue ? issue.companyId : convertible?.companyId

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let issues = await issueApi.findByCompanyId(companyId, false)
            issues = issues.filter((i) => i.id !== issue?.id)
            setIssues(issues)
        }
        void fetchData()
    }, [isAuthenticated, companyId, issue])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let convertibles = await convertibleApi.findByCompanyId(companyId)
            convertibles = convertibles.filter((c) => c.id !== convertible?.id)
            setConvertibles(convertibles)
        }
        void fetchData()
    }, [isAuthenticated, companyId, convertible])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            const issueGroups = await issuesGroupApi.findByCompanyId(companyId)
            const groups = byGroupId(issueGroups)
            setFormGroups(groups)
            setIssueGroups(groups)
        }
        void fetchData()
    }, [setIssueGroups, setFormGroups, isAuthenticated, companyId])

    if (!issues || !issueGroups) return <LinearProgress />

    const handleCreateGroup = (entities: Array<ConvertibleEntity | IssueEntity>) => {
        const entity = issue || convertible
        const groupId = uuidv4()
        const groupList = [entity, ...entities]
            .filter((e) => e !== null)
            .map((e) => {
                return { groupId, ...e, ...('type' in e ? { issueId: e.id } : { convertibleId: e.id }) }
            })
        if (groupList.length < 2) return // ignore if less than two issues
        if (isGroupInList(Object.values(issueGroups), groupList)) return // ignore if group already exists
        const newGroups = { ...issueGroups }
        newGroups[groupId] = groupList
        setFormGroups(newGroups)
        setIssueGroups(newGroups)
        setIsCustomFormDirty(true)
    }
    const handleAddToGroup = (groupId: string, issue: IssueEntity | null, convertible: ConvertibleEntity | null) => {
        const entity = issue ? { ...issue, issueId: issue.id } : { ...convertible, convertibleId: convertible.id }
        const newIssueGroups = { ...issueGroups }
        const newGroup = [...newIssueGroups[groupId], { groupId, ...entity }]
        if (isGroupInList(Object.values(issueGroups), newGroup)) return // ignore if group already exists
        newIssueGroups[groupId] = newGroup
        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }
    const handleDeleteFromGroup = (groupId: string, entityToDelete: { id: string }) => {
        const group = issueGroups[groupId]
        if (!group.some((i) => (issue ? i.issueId : i.convertibleId) === entityToDelete.id)) return // ignore if not in group
        const newIssueGroups = { ...issueGroups }
        if (group.length <= 2) {
            // delete group if only two issues left
            delete newIssueGroups[groupId]
            setFormGroups(newIssueGroups)
            setIssueGroups(newIssueGroups)
            return
        }
        newIssueGroups[groupId] = newIssueGroups[groupId].filter((e) =>
            issue ? e.issueId !== issue.id : e.convertibleId !== convertible.id,
        )
        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={6}>
                    <AllCompanyIssues
                        handleCreateGroup={handleCreateGroup}
                        issues={issues}
                        convertibles={convertibles}
                        isIssuePage={!!issue}
                    />
                </Grid>
                <Grid size={6}>
                    <IssueGroups
                        issue={issue}
                        convertible={convertible}
                        entitiesInGroups={issueGroups}
                        handleDeleteFromGroup={handleDeleteFromGroup}
                        handleAddToGroup={handleAddToGroup}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default LinkedIssuesDetails
